// A small, safe Markdown subset for lesson text: headings (##, ###), paragraphs, bullet and
// numbered lists, blockquotes, **bold**, *italic*, `code` and [links](https://…).
// It builds React elements directly (no innerHTML), so lesson text can never inject markup.
import { Fragment, type ReactNode } from 'react';

const SAFE_URL = /^(https?:\/\/|mailto:|\/)/i;

export function renderInline(text: string, keyBase = 'i'): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    const key = `${keyBase}-${k++}`;
    if (tok.startsWith('**')) out.push(<strong key={key}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) out.push(<code key={key}>{tok.slice(1, -1)}</code>);
    else if (tok.startsWith('[')) {
      const [, label, href] = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(tok) ?? [];
      if (href && SAFE_URL.test(href)) {
        const external = /^https?:/i.test(href);
        out.push(<a key={key} href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{label}</a>);
      } else out.push(label ?? tok);
    } else out.push(<em key={key}>{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Markdown({ source }: { source: string }) {
  const blocks = source.replace(/\r\n/g, '\n').split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  return (
    <div className="md">
      {blocks.map((b, i) => {
        const lines = b.split('\n');
        if (/^###\s/.test(b)) return <h3 key={i}>{renderInline(b.replace(/^###\s+/, ''), `h${i}`)}</h3>;
        if (/^##\s/.test(b)) return <h2 key={i}>{renderInline(b.replace(/^##\s+/, ''), `h${i}`)}</h2>;
        if (lines.every((l) => /^[-*]\s+/.test(l)))
          return <ul key={i}>{lines.map((l, j) => <li key={j}>{renderInline(l.replace(/^[-*]\s+/, ''), `l${i}-${j}`)}</li>)}</ul>;
        if (lines.every((l) => /^\d+\.\s+/.test(l)))
          return <ol key={i}>{lines.map((l, j) => <li key={j}>{renderInline(l.replace(/^\d+\.\s+/, ''), `l${i}-${j}`)}</li>)}</ol>;
        if (lines.every((l) => /^>\s?/.test(l)))
          return <blockquote key={i}>{renderInline(lines.map((l) => l.replace(/^>\s?/, '')).join(' '), `q${i}`)}</blockquote>;
        return (
          <p key={i}>
            {lines.map((l, j) => <Fragment key={j}>{j > 0 && <br />}{renderInline(l, `p${i}-${j}`)}</Fragment>)}
          </p>
        );
      })}
    </div>
  );
}
